import { useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useExpectedUploadedDocuments } from '@folio/utils/useExpectedUploadedDocuments';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import {
  AssetInformationCardModule,
  AuthorCardModule,
  ComplianceModule,
  CustomerInformationCardModule,
  DocumentCollectionDeadlineBanner,
  DocumentsUploadPolicyModule,
  DocumentsValidationSwitch,
  FinalizeNewBusinessModule,
  InternalNotesModule,
  InvoicesBalanceModule,
  InvoicesModule,
  InvoicesScheduleModule,
  NotificationsModule,
  PaymentMethodModule,
  PolicyholderInvitationModule,
  PolicyLifecycleSummaryModule,
  PremiumModule,
  QuoteTierModule,
  DocumentsListsPolicyModule,
  OnDemandDocumentGenerationCard,
  PolicyInformationCardModule,
  PolicyDashboardEventsModule,
  PolicyExchangesModule,
  NotFoundView,
  SignatureTrackingModule,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  EditPolicyExternalIdModule,
  DeactivatableButton,
  useToast,
} from '@korint/folio';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { bumpDocumentsRegenSignal } from '@folio/utils/documentsRegenSignal';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  keyValueFieldsToObject,
  legacyDocumentsControllerGenerateDocumentOnDemand,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerEditPolicy,
  policyControllerListPolicyDocumentsQueryOptions,
  usePolicyControllerGeneratePolicySignatureRequest,
} from 'korint-sdk';
import { useFeatureFlag, useKorintUniqConfig } from 'korint-sdk/react';
import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { PolicyDocuments as LegacyPolicyDocuments } from './legacy/PolicyDocuments';

export function PolicyHome() {
  const brokerPolicyDashboardHomeConfig = usePolicyProductConfig('brokerPolicyDashboardHome');
  const { policy } = usePolicyOnDashboard();
  const uniqConfig = useKorintUniqConfig();
  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user.brokerageFirmId;

  const { data: firmType } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    select: data => data.firmType,
    enabled: !!brokerageFirmId,
  });

  const isPolicySigned = !!policy.signedAt;

  const showModifyButton =
    !isPolicySigned ||
    Boolean(firmType && uniqConfig.policyDashboard.canModifySignedPolicy?.includes(firmType));

  if (!user?.brokerageFirmId) {
    return null;
  }

  //YEET
  // {workingBranch && <OverviewViewActionBlock />} => Check FinalizeNewBusiness from Axece
  // ✅ <PolicyholderInvitationModule />
  // <QuoteTierModule />
  // ✅ <PremiumModule />
  // <BusinessIntroducer /> => Faire un onglet dédié
  // <ComplianceView /> => Get config from the back and display conditionally
  // ✅ <PolicyLifecycleSummaryModule />

  // AXECE
  // <FinalizeNewBusiness /> => Check OverviewViewActionBlock from yeet
  // ✅ <PolicyholderInvitationModule />
  // <QuoteTierModule />
  // ✅ <PremiumModule />
  // ✅ <PolicyLifecycleSummaryModule />

  // GARANTIE PLUS
  // <WarningBanners /> => Check features
  // ✅ <AuthorCardModule />
  // <QuoteCard />
  // ✅ <PremiumModule />
  // ✅ <PolicyLifecycleSummaryModule />

  // TUTASSUR
  // ✅ <AuthorCardModule /> => A mettre partout mais avec check dans la config si le produit est retail.
  // ✅ <PolicyholderInvitationModule />
  // <FinalizeNewBusiness /> => Check OverviewViewActionBlock from yeet & axece
  // <QuoteTierModule /> => Essayer de refacto avec QuoteCArd G+
  // ✅ <PremiumModule />
  // ✅ <PolicyLifecycleSummaryModule />

  return (
    <>
      {brokerPolicyDashboardHomeConfig?.isFinalizeNewBusinessModuleVisible && (
        <FinalizeNewBusinessModule />
      )}
      <InternalNotesModule />
      <EditPolicyExternalIdModule />
      {firmType === 'WHOLESALE' && brokerPolicyDashboardHomeConfig?.isAuthorCardModuleVisible && (
        <AuthorCardModule />
      )}
      {brokerPolicyDashboardHomeConfig?.isPolicyholderInvitationModuleVisible && (
        <PolicyholderInvitationModule />
      )}
      {brokerPolicyDashboardHomeConfig?.preQuoteTierModules?.map((Module, index) => (
        <Module key={`pre-quote-tier-${index}`} />
      ))}
      {brokerPolicyDashboardHomeConfig?.isQuoteTierModuleVisible && (
        <QuoteTierModule showModifyButton={showModifyButton} />
      )}
      {brokerPolicyDashboardHomeConfig?.isPremiumModuleVisible && <PremiumModule />}
      {brokerPolicyDashboardHomeConfig?.isPolicyLifecycleSummaryModuleVisible && (
        <PolicyLifecycleSummaryModule />
      )}
      {brokerPolicyDashboardHomeConfig?.customModules?.map((Module, index) => (
        <Module key={index} />
      ))}
    </>
  );
}

export function PolicyInformation() {
  const { policy } = usePolicyOnDashboard();
  const uniqConfig = useKorintUniqConfig();
  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user?.brokerageFirmId;

  const { data: { firmType } = { firmType: undefined } } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
  });

  const isPolicySigned = !!policy.signedAt;
  const allowEdition =
    !isPolicySigned ||
    Boolean(firmType && uniqConfig.policyDashboard.canModifySignedPolicy?.includes(firmType));

  return (
    <Stack>
      <CustomerInformationCardModule allowEdition={allowEdition} />
      <PolicyInformationCardModule allowEdition={allowEdition} />
      <AssetInformationCardModule allowEdition={allowEdition} />
    </Stack>
  );
}

export function PolicyPaymentMethods() {
  return <PaymentMethodModule />;
}

function PolicyDocumentsV2() {
  const { t } = useTranslation();

  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    policy,
    workingBranch,
    hasOpenNewBusinessOrMtaBranch,
    mutate: refetchPolicy,
  } = usePolicyOnDashboard();
  const dashboardConfig = usePolicyProductConfig('brokerPolicyDashboardHome');

  const { data: policyholderDocuments } = useExpectedUploadedDocuments({
    productId: policy.productId,
    policyId: policy.id,
    branchId: workingBranch?.id,
  });

  const { data: nonDelegatedDocuments } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: data => data.nonDelegatedConfig?.documents?.documentTypes,
  });

  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user?.brokerageFirmId;

  const { data: { firmType } = { firmType: undefined } } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
  });

  // Not local state: the backend recomputes and can overwrite this field asynchronously.
  const isDocumentValidated = (policy.customFields.allClientDocumentsOk as boolean) ?? false;

  const { mutateAsync: editPolicy, isPending } = useMutation({
    mutationFn: (validated: boolean) =>
      policyControllerEditPolicy(
        policy.id,
        { customFields: [{ key: 'allClientDocumentsOk', value: validated }] },
        { branchId: workingBranch?.id },
      ),
  });

  const {
    mutateAsync: generatePolicySignatureRequest,
    isPending: isGeneratePolicySignatureRequestPending,
  } = usePolicyControllerGeneratePolicySignatureRequest();

  const { data: policyDocuments, refetch: refetchPolicyDocuments } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    select: data =>
      data.data?.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
  });

  const { mutateAsync: generateOnDemandDocument, isPending: isGeneratingDocument } = useMutation({
    mutationFn: (configName: string) =>
      legacyDocumentsControllerGenerateDocumentOnDemand({
        productId: policy.productId,
        configName,
        targetId: policy.id,
        ...(workingBranch?.id ? { branchId: workingBranch.id } : {}),
      }),
    onSuccess: () => {
      refetchPolicyDocuments();
      // The document lands in the list a beat after the response; this polls until it shows.
      bumpDocumentsRegenSignal(queryClient, policy.id);
      toast.toast({ title: t('documents.generateSuccess') });
    },
    onError: () => {
      toast.toast({ title: t('documents.generateError') });
    },
  });

  const { data: nonDelegatedDocumentsRequiredForSignature = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy?.productId!),
    enabled: Boolean(policy?.productId),
    select: ({ signature, nonDelegatedConfig }) =>
      nonDelegatedConfig?.documents?.documentTypes.filter(category =>
        signature.signedDocuments.find(
          ({ type, signature, neededForPolicySigned }) =>
            type === category && !neededForPolicySigned && signature === 'SIGN',
        ),
      ),
  });

  const documentConformityFlagEnabled = useFeatureFlag(
    'feat_document_conformity',
    policy.productId,
  );

  // Kept for products without document conformity: ~23 funnels rely on allClientDocumentsOk.
  const validateSwitch =
    dashboardConfig.disableDocumentsValidation || documentConformityFlagEnabled
      ? undefined
      : {
          checked: isDocumentValidated,
          onCheckedChange: async (checked: boolean) => {
            await editPolicy(checked);
            await refetchPolicy();
          },
          disabled: isPending,
          label: t('documentsValidation.validateLabel'),
        };

  return (
    <Stack>
      {validateSwitch && !policyholderDocuments?.length && (
        <DocumentsValidationSwitch {...validateSwitch} standalone />
      )}

      <DocumentCollectionDeadlineBanner policyId={policy.id} branchId={workingBranch?.id} />

      <DocumentsListsPolicyModule displayContext="broker" />

      <OnDemandDocumentGenerationCard
        onGenerate={configName => generateOnDemandDocument(configName)}
        isGenerating={isGeneratingDocument}
      />

      {Boolean(nonDelegatedDocuments?.length) && firmType === 'WHOLESALE' && (
        <DocumentsUploadPolicyModule
          displayContext="broker"
          documentsLists={
            nonDelegatedDocuments?.map(category => ({ category, documentType: category })) ?? []
          }
          categoryType="nonDelegatedDocuments"
          useBranchId={Boolean(workingBranch?.id)}
          extraActions={
            <Group gap={2}>
              <DeactivatableButton
                onClick={async () => {
                  await generatePolicySignatureRequest({
                    policyId: policy.id,
                    data: { deliveryMode: 'EMAIL', neededForPolicySigned: false },
                    params: { branchId: workingBranch?.id },
                  });

                  toast.toast({
                    title: 'Demande de signature envoyée',
                    description: 'Les documents ont été envoyés en signature avec succès.',
                  });
                }}
                loading={isGeneratePolicySignatureRequestPending}
                rules={[
                  {
                    isValid: Boolean(workingBranch?.id) || !hasOpenNewBusinessOrMtaBranch,
                    message:
                      "Pour uploader les documents liés à un avenant, rendez-vous sur l'avenant concerné via le bouton « Explorer les avenants ».",
                  },
                  ...nonDelegatedDocumentsRequiredForSignature.map(documentType => {
                    const documentsOfType = (policyDocuments ?? []).filter(
                      ({ metadata, type, category }) =>
                        type === 'non_delegated' &&
                        category === documentType &&
                        metadata.archived === 'false',
                    );

                    const isMissingSignature = documentsOfType.some(
                      doc => doc.metadata.signed === undefined || doc.metadata.signed === 'false',
                    );

                    return {
                      isValid: documentsOfType.length > 0 && isMissingSignature,
                      message: `En attente du téléversement du document "${t(
                        `documents.${documentType}`,
                      )}".`,
                    };
                  }),
                ]}
              >
                Envoyer en signature
              </DeactivatableButton>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="ml-2 h-4 w-4 text-muted-foreground cursor-help font-normal" />
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-sm">
                    La signature de ces documents n'est pas obligatoire pour la finalisation du
                    devis.
                  </div>
                </TooltipContent>
              </Tooltip>
            </Group>
          }
          authorizedFormats={{
            'application/pdf': ['.pdf'],
          }}
        />
      )}

      {Boolean(policyholderDocuments?.length) && (
        <DocumentsUploadPolicyModule
          displayContext="broker"
          documentsLists={policyholderDocuments ?? []}
          useBranchId={Boolean(workingBranch?.id)}
          hideAcceptAllButton={Boolean(validateSwitch)}
          extraActions={validateSwitch && <DocumentsValidationSwitch {...validateSwitch} />}
        />
      )}
    </Stack>
  );
}

export function PolicyDocuments() {
  const documentReviewEnabled = useFeatureFlag('feat_document_review');
  return documentReviewEnabled ? <PolicyDocumentsV2 /> : <LegacyPolicyDocuments />;
}

export function PolicyInvoices() {
  const dashboardConfig = usePolicyProductConfig('brokerPolicyDashboardHome');

  return (
    <Stack>
      {dashboardConfig?.isInvoicesBalanceModuleVisible && <InvoicesBalanceModule />}
      <InvoicesModule />
      <InvoicesScheduleModule />
    </Stack>
  );
}

export function PolicyEvents() {
  return (
    <Stack>
      <PolicyDashboardEventsModule stopFunnelBasePath="/policies" />
      <SignatureTrackingModule />
    </Stack>
  );
}

export function PolicyNotifications() {
  return <NotificationsModule />;
}

export function PolicyCompliance() {
  return <ComplianceModule />;
}

export function PolicyExchanges() {
  const { policyId } = useAssertedParams();
  const enabled = useFeatureFlag('feat_connected_email_accounts');
  return enabled ? <PolicyExchangesModule key={policyId} policyId={policyId} /> : <NotFoundView />;
}
